/** @file Settings › General: interface language (settings.ui.lang), theme (dark only) and the layout reset. */
import { catalogLanguages } from '@pose2d/i18n/catalogLanguages';
import type { Translate } from '@pose2d/i18n/types';
import { useState } from 'react';
import { useStore } from 'zustand';
import { api } from '../../api/api';
import { catalogs } from '../../shell/i18n/catalogs';
import { errorText } from '../../shell/i18n/errorText';
import { updateUiSettings } from '../../shell/settings/updateUiSettings';
import { settingsStore } from '../../stores/settingsStore';
import type { SettingsSlice } from '../../stores/types';

const LANGUAGES = catalogLanguages(catalogs);

/** Language and layout reset save through settings.ui; a failed save shows an alert until the next save starts. */
export function GeneralSection({ t }: { t: Translate }) {
  const lang = useStore(settingsStore, (s) => s.lang);
  const [failure, setFailure] = useState<{ thrown: unknown } | null>(null);
  const save = (patch: Partial<SettingsSlice>): void => {
    setFailure(null);
    updateUiSettings(api, patch).then(() => { setFailure(null); }, (thrown: unknown) => { setFailure({ thrown }); });
  };
  return (
    <section className="shell-card shell-form settings-section" aria-labelledby="settings-general">
      <h2 id="settings-general">{t('settings.general')}</h2>
      <label>
        {t('settings.language')}
        <select value={lang} onChange={(e) => { save({ lang: e.target.value }); }}>
          {LANGUAGES.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
        </select>
      </label>
      <p className="shell-hint">{t('settings.languageHint')}</p>
      <p>{t('settings.theme')}: {t('settings.themeDark')}</p>
      <button type="button" onClick={() => { save({ layout: null }); }}>{t('settings.resetLayout')}</button>
      {failure === null ? null : <p role="alert" className="shell-error">{errorText(t, failure.thrown)}</p>}
    </section>
  );
}
